import {
  useState, useEffect, useCallback, createContext,
} from 'react';
import { useLocation } from 'react-router';

export type AddSource = {
  url: string;
  onMessage: (data: Message) => void;
  onOpen?: () => void;
  onError?: () => void;
};

type Message = {
  action: Action;
  time: number;
};

type sseContextType = {
  addSource: (sourceData: AddSource) => void;
};

type sseState = {
  pageUrl: string;
  sources: EventSource[];
};

const initialState = {
  addSource: () => {},
};

export const SseContext = createContext<sseContextType>(initialState);

const createSource = (sourceData: AddSource, sourceUrl: string) => {
  const source = new EventSource(sourceUrl);
  if (sourceData.onOpen) {
    source.onopen = sourceData.onOpen;
  }
  if (sourceData.onError) {
    source.onerror = sourceData.onError;
  }
  source.onmessage = (msg) => {
    try {
      sourceData.onMessage(JSON.parse(msg.data));
    } catch (e) {
      console.error(e);
    }
  };

  return source;
};

export const useSSE = (): sseContextType => {
  const [sources, setSource] = useState<sseState[]>([]);
  const location = useLocation();

  const closeForPageUrl = useCallback((pageUrl: string) => {
    const index = sources.findIndex((item) => item.pageUrl === pageUrl);
    if (index !== -1 && sources[index].sources.length > 0) {
      sources[index].sources.forEach((source) => source.close());
      sources.splice(index, 1);
      setSource(sources);
    }
  }, [sources, setSource]);

  useEffect(() => () => {
    const pageUrl = location.pathname + location.search;
    closeForPageUrl(pageUrl);
  }, [location, sources, closeForPageUrl]);

  const addSource = useCallback((sourceData: AddSource) => {
    if (window.EventSource) {
      if (sourceData.url.trim() === '') {
        return;
      }
      const pageUrl = location.pathname + location.search;
      const sourceUrl = `${process.env.REACT_APP_SSE}${sourceData.url}`;
      const pageSource = sources.find(
        (item) => item.pageUrl === pageUrl,
      );

      if (pageSource === undefined) {
        const source = createSource(sourceData, sourceUrl);
        sources.push({
          pageUrl,
          sources: [source],
        });
        setSource(sources);
      } else {
        // Add new SSE for pageSource (route)
        const isExist = pageSource.sources.find((item) => item.url === sourceUrl);
        if (isExist === undefined) {
          const source = createSource(sourceData, sourceUrl);
          pageSource.sources.push(source);
          setSource(sources);
        }
      }
    }
  }, [sources, setSource, location]);

  return {
    addSource,
  };
};

type Action = 'updated'
| 'deleted'
| 'processing_completed'
| 'child_structure_updated'
| 'child_content_updated'

| 'video.master.preparing' // Master request events
| 'video.master.ready';
